// LEA-518 — first contact-pack purchase for reveal customers.
//
// The one-click refill (ContactPackRefillCard) pays with a card saved by an
// earlier pack Checkout, so it is never offered for a customer's first purchase.
// Without this card, a new reveal customer who runs out of contacts lands on
// Account -> Billing with nothing to buy. Here they pick a pack and pay in Stripe
// Checkout. The Checkout saves the card, so their next purchase can be the
// one-click refill.
//
// Rendered by ContactPackRefillCard when the refill isn't offered to a reveal
// customer. Prices, the first-purchase discount and which packs exist come from
// GET /contact-packs; the checkout route decides eligibility again on its own,
// so the client never computes an amount.

import { formatNumber, t, useLang } from '@cortex/i18n';
import type { ContactPackSize, ContactPacksResponse } from '@cortex/schemas';
import { useCallback, useEffect, useState } from 'react';
import { getContactPacks, startContactPackCheckout } from '../../lib/billing';

// Same formatting as the refill card: always with cents.
function money(cents: number, lang: string): string {
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

type Load = 'loading' | 'ready' | 'error';

export default function ContactPackFirstPurchaseCard() {
  const lang = useLang();
  const [offer, setOffer] = useState<ContactPacksResponse | null>(null);
  const [load, setLoad] = useState<Load>('loading');
  const [packSize, setPackSize] = useState<ContactPackSize | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(false);

  const fetchOffer = useCallback(async (isCancelled: () => boolean) => {
    setLoad('loading');
    try {
      const res = await getContactPacks();
      if (isCancelled()) return;
      setOffer(res);
      setLoad('ready');
      const first = res.packs.find((p) => p.configured);
      setPackSize((current) => current ?? first?.contacts ?? null);
    } catch {
      if (!isCancelled()) setLoad('error');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchOffer(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchOffer]);

  const buy = useCallback(async () => {
    if (!packSize || redirecting) return;
    setRedirecting(true);
    setError(false);
    try {
      const { url } = await startContactPackCheckout(packSize);
      if (!url) throw new Error('checkout_url_missing');
      // Keep the busy state: the page is leaving, a second click must not open
      // a second Checkout.
      window.location.assign(url);
    } catch {
      setRedirecting(false);
      setError(true);
    }
  }, [packSize, redirecting]);

  const packs = offer?.packs.filter((p) => p.configured) ?? [];

  if (load === 'loading') return null;
  if (load === 'ready' && packs.length === 0) return null;

  return (
    <div className="card-base overflow-hidden">
      <div className="px-6 py-4 border-b border-[#F0F4FA]">
        <div className="card-title">{t('packs.first.title')}</div>
        <div className="text-[12.5px] text-ink-muted leading-snug mt-0.5">
          {t('packs.first.subtitle')}
        </div>
      </div>

      {load === 'error' ? (
        <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div role="alert" className="text-[13px] text-red-700">
            {t('packs.first.loadError')}
          </div>
          <button
            type="button"
            onClick={() => void fetchOffer(() => false)}
            className="btn btn-md btn-secondary whitespace-nowrap w-full sm:w-auto"
          >
            {t('packs.first.retry')}
          </button>
        </div>
      ) : (
        <div className="px-6 py-5 flex flex-col gap-4">
          {offer && (
            <div className="text-[13px] text-ink">
              {t('packs.first.balance', { n: formatNumber(offer.contactsRemaining, lang) })}
            </div>
          )}

          <fieldset className="grid grid-cols-1 sm:grid-cols-3 gap-2" disabled={redirecting}>
            <legend className="sr-only">{t('refill.choose')}</legend>
            {packs.map((p) => {
              const active = p.contacts === packSize;
              return (
                <button
                  key={p.contacts}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    if (!active && !redirecting) {
                      setError(false);
                      setPackSize(p.contacts);
                    }
                  }}
                  className={`text-left rounded-lg border px-4 py-3 transition-colors disabled:opacity-60 ${
                    active ? 'border-brand-500 bg-brand-50' : 'border-line hover:border-ink-soft'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13.5px] font-medium text-ink whitespace-nowrap">
                      {t('refill.pack', { n: formatNumber(p.contacts, lang) })}
                    </span>
                    {p.popular && (
                      <span className="text-[11px] font-medium text-brand-700 bg-brand-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                        {t('packs.first.popular')}
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-ink-muted whitespace-nowrap">
                    {p.discountCents > 0 && (
                      <span className="line-through mr-1">{money(p.priceCents, lang)}</span>
                    )}
                    {t('refill.net', { amount: money(p.effectivePriceCents, lang) })}
                  </div>
                </button>
              );
            })}
          </fieldset>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-[12px] text-ink-muted leading-snug min-w-0">
              {t('packs.first.checkoutNote')}
            </div>
            <button
              type="button"
              onClick={() => void buy()}
              disabled={!packSize || redirecting}
              className="btn btn-md btn-primary whitespace-nowrap w-full sm:w-auto"
            >
              {redirecting ? t('packs.first.redirecting') : t('packs.first.cta')}
            </button>
          </div>

          <div className="text-[12px] text-ink-muted">{t('refill.terms')}</div>

          {error && (
            <div role="alert" className="text-[12.5px] rounded-lg px-3 py-2 bg-red-50 text-red-700">
              {t('packs.first.error')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
